import { Trans, useTranslation } from 'react-i18next';
import { useGetBenefitDetail } from '@/common/integrations/menhir/employee/benefits/benefits.hooks';
import { IOperation } from '@/common/integrations/menhir/employee/operations/operations.types';
import { formatDate, formatTime } from '@/common/utils/date';
import { FailedAction } from '@/web/components/TransactionList/components/OperationDetail/components/FailedDetailDrawer/components/FailedAction/FailedAction';
import { OperationDetailDrawer } from '@/web/components/TransactionList/components/OperationDetail/components/OperationDetailDrawer/OperationDetailDrawer';
import { OperationSummaryDetail } from '@/web/components/TransactionList/components/OperationDetail/components/OperationSummaryDetail/OperationSummaryDetail';
import {
  Flex,
  IconBan,
  LabeledValueList,
  Text,
  themeVars,
} from '@coverflex-tech/hypernova';
import { MoneyUtils, TMoney } from '@coverflex-tech/hypernova-utils';
import { css } from './FailedDetailDrawer.css';

interface IFailedDetailProps {
  operation: IOperation;
  toggleLearningCentre: (value: boolean) => void;
}

export const FailedDetailDrawer = ({
  operation,
  toggleLearningCentre,
}: IFailedDetailProps) => {
  const { t } = useTranslation();

  const isBenefit =
    operation.categorySlug === 'fringe' || operation.categorySlug === 'meals';

  const { benefit } = useGetBenefitDetail(
    {
      slug: operation.categorySlug!,
    },
    { enabled: isBenefit }
  );

  const rejectionReason = operation.descriptionParams.find(
    (param) => param.key === 'rejection_reason'
  )?.value;

  const voucherValue = operation.descriptionParams.find(
    (param) => param.key === 'voucher_value'
  )?.value as TMoney;

  const remainingValue = operation.descriptionParams.find(
    (param) => param.key === 'remaining_value'
  )?.value as TMoney;

  const voucherCount = operation.descriptionParams.find(
    (param) => param.key === 'voucher_count'
  )?.value as number;

  return (
    <OperationDetailDrawer
      title={t('general.labels.transaction')}
      operation={operation}
    >
      <Flex flexDirection="column" justifyContent="space-between" flexGrow={1}>
        <Flex flexDirection="column" gap="xl">
          <OperationSummaryDetail
            icon={<IconBan color={themeVars.color.semantic.text.systemError} />}
            operation={operation}
          />
          <LabeledValueList.Root title="">
            <LabeledValueList.Item
              label={t('general.labels.date_and_time')}
              value={t('general.labels.date_and_time.value', {
                date: formatDate(operation.executedAt),
                time: formatTime(operation.executedAt),
              })}
            />
            {voucherCount && (
              <LabeledValueList.Item
                label={t('operations.payable_with_vouchers.label')}
                value={t('operations.payable_with_vouchers.value', {
                  count: voucherCount,
                  voucherCount,
                  voucherValue: MoneyUtils.moneyToString(voucherValue),
                })}
              />
            )}
            {operation.usedEmployeeCreditAmount ? (
              <LabeledValueList.Item
                label={t('operations.payable_with_credits.label')}
                value={MoneyUtils.moneyToString(
                  operation.usedEmployeeCreditAmount
                )}
              />
            ) : null}
            {remainingValue && (
              <LabeledValueList.Item
                label={t('operations.remaining_value.label')}
                value={MoneyUtils.moneyToString(remainingValue)}
              />
            )}
          </LabeledValueList.Root>
          {rejectionReason && (
            <Trans
              i18nKey={`rejection_reason.${rejectionReason}`}
              values={{
                voucherValue:
                  isBenefit && MoneyUtils.moneyToString(voucherValue),
                fringeLimit:
                  isBenefit && MoneyUtils.moneyToString(benefit?.usage?.limit),
                merchantName:
                  'merchantName' in operation && operation.merchantName,
              }}
              parent={'div'}
              components={{
                heading: (
                  <Text bold size="lg">
                    -
                  </Text>
                ),
                ul: <ul className={css.list} />,
                li: <li className={css.listItem} />,
              }}
            />
          )}
        </Flex>
        {rejectionReason && (
          <FailedAction
            errorTag={rejectionReason as string}
            toggleLearningCentre={toggleLearningCentre}
          />
        )}
      </Flex>
    </OperationDetailDrawer>
  );
};
